import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import findKey from 'lodash/findKey';
// import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { PageTitle, PageContent, Tab, Menu, Flex, Link } from 'nallian-shared-ui/lib/components';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { GetRights, GetMyPrincipal } from 'nallian-shared-ui/lib/selectors';
import { hasSomeRights, hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import principalService from '../../services/principals/principalService';
import { TABS, PRINCIPAL_TABS, PRINCIPAL_ACTIONS } from '../../constants';
import { isSystemOrganisation } from '../../../../app/config/systemOrgs';
import PrincipalIdentitiesTab from './identities/PrincipalIdentities';
import PrincipalInformation from './details/PrincipalInformation';
import PrincipalRoles from './details/PrincipalRoles';
import EditPrincipal from './EditPrincipal';
import { useCanAccessDocuments } from '../../hooks/useCanAccessDocuments';
import PrincipalDocumentsTab from './documents/PrincipalDocumentsTab';

const PRINCIPAL_RIGHTS = [
  'PRINCIPALS.MANAGE.ALL',
  'PRINCIPALS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.MANAGE.ORGANISATION',
];

const PrincipalDetail = ({ dispatchPrincipalGet, principalFetch }) => {
  const { t } = useTranslation();
  const { id, actionId, tab, action } = useParams();
  const navigate = useNavigate();

  useEffect(() => dispatchPrincipalGet(actionId, t), [actionId, t, dispatchPrincipalGet]);

  const principal = principalFetch.value;

  const urlRoot = useMemo(
    () =>
      id
        ? `/organisations/${id}/${TABS.PRINCIPALS}/${PRINCIPAL_ACTIONS.DETAILS}/${actionId}`
        : `/organisation/${TABS.PRINCIPALS}/${PRINCIPAL_ACTIONS.DETAILS}/${actionId}`,
    [actionId, id]
  );

  const rights = useSelector(GetRights);
  const myPrincipal = useSelector(GetMyPrincipal);
  const organisationId = id || myPrincipal?.organisation?.id;

  const isSystemOrg = useMemo(() => isSystemOrganisation(organisationId), [organisationId]);

  const { read: canReadDocuments, manage: canManageDocuments } = useCanAccessDocuments(
    organisationId,
    actionId
  );

  const TAB_INDEX_MAPPING = useMemo(() => {
    const mapping = {
      [PRINCIPAL_TABS.DETAILS]: 0,
      [PRINCIPAL_TABS.ROLES]: 1,
      [PRINCIPAL_TABS.ACCOUNTS]: 2,
    };
    if (canReadDocuments) {
      mapping[PRINCIPAL_TABS.DOCUMENTS] = 3;
    }
    return mapping;
  }, [canReadDocuments]);

  const forbidManageBecauseSystemOrg = useMemo(
    () =>
      isSystemOrganisation(organisationId) &&
      !hasRight(rights, 'ORGANISATIONS.MANAGE.ORGANISATION') &&
      hasSomeRights(rights, [
        'ORGANISATIONS.MANAGE.ALL',
        'ORGANISATIONS.MANAGE.ALL.EXCEPTSYSTEMORGS',
      ]),
    [rights, organisationId]
  );

  const readOnly = useMemo(
    () => forbidManageBecauseSystemOrg || !hasSomeRights(rights, PRINCIPAL_RIGHTS),
    [rights, forbidManageBecauseSystemOrg]
  );

  return (
    <PageContent>
      <PageTitle flexed>
        {id ? (
          <Flex alignItems="center">
            <Link to="/organisations" marginright="1px">
              {t('organisations')}
            </Link>
            &nbsp;/&nbsp;
            <Link to={`/organisations/${id}/${TABS.DETAILS}`} marginright="1px">
              {principal?.organisation?.name}
            </Link>
            &nbsp;/&nbsp;
            <Link to={`/organisations/${id}/${TABS.PRINCIPALS}`} marginright="1px">
              {t('users')}
            </Link>
            &nbsp;/&nbsp;
            {principal?.fullName}
          </Flex>
        ) : (
          <Flex alignItems="center">
            <Link to="/organisation" marginright="1px">
              {t('myOrganisation')}
            </Link>
            &nbsp;/&nbsp;
            <Link to="/organisation/users" marginright="1px">
              {t('users')}
            </Link>
            &nbsp;/&nbsp;
            {principal?.fullName}
          </Flex>
        )}
      </PageTitle>
      <EditPrincipal
        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
        urlRoot={urlRoot}
        organisationId={organisationId}
        isSystemOrg={isSystemOrg}
        open={tab === PRINCIPAL_ACTIONS.EDIT}
        id={actionId}
      />
      <Tab
        activeIndex={TAB_INDEX_MAPPING[tab]}
        onTabChange={(e, { activeIndex }) => {
          const activeTab = findKey(TAB_INDEX_MAPPING, index => index === activeIndex);
          if (activeTab) {
            navigate(`${urlRoot}/${activeTab}`);
          }
        }}
        panes={[
          {
            menuItem: t('details'),
            render: () => (
              <Tab.Pane>
                <PrincipalInformation principal={principal} />
              </Tab.Pane>
            ),
          },
          {
            menuItem: t('roles'),
            render: () => (
              <Tab.Pane>
                <PrincipalRoles principal={principal} />
              </Tab.Pane>
            ),
          },
          {
            menuItem: t('accounts'),
            render: () => (
              <Tab.Pane>
                <PrincipalIdentitiesTab
                  isSystemOrg={false}
                  readOnly={readOnly}
                  identities={principal?.principalIdentities}
                  data={principal}
                  id={actionId}
                  isActive
                />
              </Tab.Pane>
            ),
          },
          canReadDocuments
            ? {
                menuItem: t('documents'),
                render: () => (
                  <Tab.Pane>
                    <PrincipalDocumentsTab
                      principalId={actionId}
                      canManage={canManageDocuments}
                      urlRoot={urlRoot}
                      action={action}
                    />
                  </Tab.Pane>
                ),
              }
            : undefined,
          {
            menuItem: (
              <Menu.Menu position="right" key="menu">
                <HasSomeRights rights={PRINCIPAL_RIGHTS}>
                  {!forbidManageBecauseSystemOrg ? (
                    <Menu.Item
                      icon="pencil"
                      content={t('edit')}
                      onClick={() => navigate(`${urlRoot}/edit`)}
                    />
                  ) : (
                    <div />
                  )}
                </HasSomeRights>
              </Menu.Menu>
            ),
          },
        ]}
      />
    </PageContent>
  );
};

PrincipalDetail.propTypes = {
  dispatchPrincipalGet: PropTypes.func.isRequired,
  principalFetch: PropTypes.object.isRequired,
};

export default principalService(PrincipalDetail);
